import React, {useEffect, useState, useMemo} from "react"
import * as am4core from "@amcharts/amcharts4/core"
import * as am4charts from "@amcharts/amcharts4/charts"
import am4themesMaterial from "@amcharts/amcharts4/themes/material"
import am4themesDark from "@amcharts/amcharts4/themes/dark"
import am4themesAnimated from "@amcharts/amcharts4/themes/animated"
import {red, green, blue, grey} from "@material-ui/core/colors"
import {makeStyles} from "@material-ui/core/styles"
import {useStore, useDispatch} from "../Store"
import {getTrendSimilarityList} from "../api"
import {getTokenCookie} from "../common"
import {useTranslation} from "react-i18next"
import "../i18n"
import {useCookies} from "react-cookie"
import {useMediaQuery} from "@material-ui/core"
import {useTheme} from "../hooks"

const useStyles = makeStyles(() => ({
  root: {
    display: `grid`,
    gridTemplateColumns: (props) => (props.isMobile ? `repeat(2, 1fr)` : `repeat(4, 1fr)`),
    width: `100%`,
    zIndex: (props) => (props.zIndex === `trendChart` ? 1201 : 0),
  },
}))

export default function TrendChart({closing}) {
  let chartReg = {}
  const [cookies] = useCookies([])
  const {t} = useTranslation()
  const {
    store: {
      trendSimilarityList,
      zIndex,
      themeColor,
      perspective,
      tabs,
      trendList,
      locale,
      mainList,
      mainTarget,
      mainBaseDate,
      mainTerm,
      mainType,
      mainGroup,
    },
  } = useStore()
  const theme = useTheme(themeColor)
  const isMobile = useMediaQuery(theme.breakpoints.down(`xs`))
  const classes = useStyles({
    isMobile,
    zIndex,
  })
  const {dispatch} = useDispatch()
  const [containerList, setContainerList] = useState([])
  const [valueAxisXList] = useState([])
  const redColor = red[500]
  const greenColor = green.A700
  const blueColor = blue.A700
  const gridStrokeColor = grey[500]
  const gridBackGroundColor = 0.01

  const createDom = async (trendList) => {
    const containerList = trendList.map((trend) => {
      const chartDiv = `trendID-${trend.trendNo}`
      return <div id={chartDiv} key={chartDiv} />
    })
    setContainerList(containerList)
    return containerList
  }

  const disposeChart = (chartDiv) => {
    if (chartReg[chartDiv]) {
      chartReg[chartDiv].dispose()
      delete chartReg[chartDiv]
    }
  }

  const disposeCharts = () => {
    trendList.map((trend) => {
      const chartDiv = `trendID-${trend.trendNo}`
      disposeChart(chartDiv)
    })
  }

  const createChart = (chartDiv, trend) => {
    disposeChart(chartDiv)
    chartReg[chartDiv] = am4core.create(chartDiv, am4charts.XYChart)
    chartReg[chartDiv].showOnInit = false
    if (themeColor === `dark`) am4core.useTheme(am4themesDark)
    if (themeColor !== `dark`) am4core.useTheme(am4themesMaterial)
    am4core.useTheme(am4themesAnimated)
    chartReg[chartDiv].cursor = new am4charts.XYCursor()
    chartReg[chartDiv].cursor.behavior = isMobile ? `none` : `panXY`
    chartReg[chartDiv].cursor.lineX.opacity = 0
    chartReg[chartDiv].cursor.lineY.opacity = 0
    chartReg[chartDiv].svgContainer.htmlElement.style.height = `200px`
    chartReg[chartDiv].data = trend.data
    // chartReg[chartDiv].exporting.menu = new am4core.ExportMenu()
    return chartReg[chartDiv]
  }

  const createValueAxisX = (chart, chartDiv) => {
    const valueAxisX = chart.xAxes.push(new am4charts.ValueAxis())
    valueAxisX.tooltip.disabled = true
    valueAxisX.renderer.labels.template.disabled = true
    valueAxisX.renderer.grid.template.stroke = am4core.color(gridStrokeColor)
    valueAxisX.renderer.gridContainer.background.fillOpacity = gridBackGroundColor
    valueAxisX.min = 0
    valueAxisX.max = 1
    valueAxisX.title.marginTop = 10
    valueAxisX.title.fill = themeColor === `dark` ? am4core.color(grey[50]) : am4core.color(grey[900])
    valueAxisXList[chartDiv] = valueAxisX
  }

  const createValueAxisY = (chart) => {
    const valueAxisY = chart.yAxes.push(new am4charts.ValueAxis())
    valueAxisY.tooltip.disabled = true
    valueAxisY.renderer.labels.template.disabled = true
    valueAxisY.renderer.grid.template.stroke = am4core.color(gridStrokeColor)
    valueAxisY.renderer.gridContainer.background.fillOpacity = gridBackGroundColor
    valueAxisY.min = 0
    valueAxisY.max = 1
  }

  const createLineSeries = (chart, trend) => {
    const lineSeries = chart.series.push(new am4charts.LineSeries())
    lineSeries.dataFields.valueX = `x`
    lineSeries.dataFields.valueY = `y`
    lineSeries.tooltipText = `value: {valueY.value}`
    lineSeries.strokeWidth = 2.0
    lineSeries.fillOpacity = 0.0
    const lineColor = trend.trendType === `UP` ? redColor : trend.trendType === `DOWN` ? greenColor : blueColor
    lineSeries.stroke = am4core.color(lineColor)
  }

  useEffect(() => {
    const fetchDom = async () => {
      if (!trendList.length) return
      if (!containerList.length) await createDom(trendList)
      trendList.map((trend) => {
        const chartDiv = `trendID-${trend.trendNo}`
        const chart = createChart(chartDiv, trend)
        createValueAxisX(chart, chartDiv)
        createValueAxisY(chart)
        createLineSeries(chart, trend)
        const title = locale === `ja` ? trend.labelJa : trend.labelEn
        valueAxisXList[chartDiv].title.text = `${title}\n${t(`similarity`)} XX.XX%`
      })
    }
    fetchDom().then()
    return () => {
      disposeCharts()
    }
  }, [trendList])

  useEffect(() => {
    if (!mainTarget[perspective].key) return
    if (!mainTerm[perspective].key) return
    if (!mainBaseDate[perspective].key) return
    const isFx = mainType[perspective] === `FX` || mainType[perspective] === `FXSET`
    const fetchData = async () => {
      let code = null,
        exchange = null,
        region = null
      if (isFx) {
        code = mainTarget[perspective].key
      } else {
        const record = mainList[perspective].find((data) => data.code === mainTarget[perspective].key)
        code = record.code
        exchange = record.exchange
        region = record.region
      }
      const params = {
        type: mainType[perspective],
        group: mainGroup[perspective],
        baseDate: mainBaseDate[perspective].key,
        term: mainTerm[perspective].key,
        code,
        exchange,
        region,
      }
      const data = await getTrendSimilarityList(getTokenCookie(cookies), params)
      const result = data.data ?? []
      trendSimilarityList[perspective] = result
      dispatch({
        type: `GET_TREND_SIMILARITY_LIST`,
        trendSimilarityList,
      })
      return trendSimilarityList
    }
    
    fetchData().then((trendSimilarityList) => {
      if (trendSimilarityList[perspective].length === 0) {
        dispatch({
          type: `LOADING_MODAL`,
          loadingModal: {
            state: false,
          },
        })
        const confirmDialog = {
          message: t(`messageNotFoundData`),
          error: ``,
          state: true,
        }
        dispatch({
          type: `CONFIRM_DIALOG`,
          confirmDialog,
        })
        return
      }
      const trendMergeList = trendSimilarityList[perspective].map((trendSimilarity) => {
        const trend = trendList.find((trend) => trend.trendNo === trendSimilarity.trendNo)
        const chartDiv = `trendID-${trend.trendNo}`
        const title = locale === `ja` ? trend.labelJa : trend.labelEn
        const similarity = Math.round(trendSimilarity.similarity * 10000) / 100
        valueAxisXList[chartDiv].title.text = `${title}\n${t(`similarity`)} ${similarity}%`
        return {
          ...trend,
          similarity: trendSimilarity.similarity,
        }
      })
      trendSimilarityList[perspective] = trendMergeList.sort((a, b) => {
        if (a.similarity > b.similarity) return 1
        if (a.similarity < b.similarity) return -1
        return 0
      })
      dispatch({
        type: `GET_TREND_SIMILARITY_LIST`,
        trendSimilarityList,
      })
    })
  }, [mainTarget[perspective].key, mainTerm[perspective].key, mainBaseDate[perspective].key])

  useEffect(() => {
    if (closing) {
      disposeCharts()
    }
  }, [trendList, closing])

  return useMemo(
    () => (
      <div id={`trendChart`} className={classes.root}>
        {containerList}
      </div>
    ),
    [containerList, classes],
  )
}
